import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// ====================================
// Types
// ====================================

interface TrashCourse {
  id: number
  title: string
  description: string
  slug: string
  deletedAt: string
  deletedBy: string
  totalModulesCount: number
  deletedModulesCount: number
  totalEpisodesCount: number
  deletedEpisodesCount: number
  _count: {
    enrollments: number
  }
}

interface TrashModule {
  id: number
  title: string
  description: string
  deletedAt: string
  deletedBy: string
  totalEpisodesCount: number
  deletedEpisodesCount: number
  course: {
    id: number
    title: string
    slug: string
  }
}

interface TrashEpisode {
  id: number
  title: string
  description: string | null
  deletedAt: string
  deletedBy: string
  userProgressCount: number
  module: {
    id: number
    title: string
    course: {
      id: number
      title: string
      slug: string
    }
  }
}

// ====================================
// API Functions
// ====================================

async function listTrashCourses(): Promise<TrashCourse[]> {
  const response = await api.get('/courses/trash')
  return response.data
}

async function restoreCourse(courseId: number): Promise<void> {
  await api.post(`/courses/${courseId}/restore`)
}

async function listTrashModules(): Promise<TrashModule[]> {
  const response = await api.get('/modules/trash')
  return response.data
}

async function restoreModule(moduleId: number): Promise<void> {
  await api.post(`/modules/${moduleId}/restore`)
}

async function listTrashEpisodes(): Promise<TrashEpisode[]> {
  const response = await api.get('/episodes/trash')
  return response.data
}

async function restoreEpisode(episodeId: number): Promise<void> {
  await api.post(`/episodes/${episodeId}/restore`)
}

// ====================================
// React Query Hooks
// ====================================

/**
 * Lista cursos na lixeira
 */
export function useListTrashCourses() {
  return useQuery({
    queryKey: ['trash', 'courses'],
    queryFn: listTrashCourses,
  })
}

/**
 * Restaura um curso
 */
export function useRestoreCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreCourse,
    onSuccess: () => {
      // Invalida todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['trash'] })
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
    },
  })
}

/**
 * Lista módulos na lixeira
 */
export function useListTrashModules() {
  return useQuery({
    queryKey: ['trash', 'modules'],
    queryFn: listTrashModules,
  })
}

/**
 * Restaura um módulo
 */
export function useRestoreModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreModule,
    onSuccess: () => {
      // Invalida todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['trash'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
    },
  })
}

/**
 * Lista episódios na lixeira
 */
export function useListTrashEpisodes() {
  return useQuery({
    queryKey: ['trash', 'episodes'],
    queryFn: listTrashEpisodes,
  })
}

/**
 * Restaura um episódio
 */
export function useRestoreEpisode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreEpisode,
    onSuccess: () => {
      // Invalida todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['trash'] })
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
    },
  })
}
